import { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useAcfuProgressiveDisclosure, useDateFormat, useFeatureFlags, useNow } from '../../../../../../hooks'
import { AppointmentFormData, AvailabilitiesResponse } from '../../../../../../types'
import { Calendar } from '../../../../../forms'
import { addDays } from 'date-fns'
import { startOfDayTimezoned } from 'util/startOfDayTimezoned'
import { CustomPickersDay } from 'components/CustomPickersDay'
import { PickersDayProps } from '@mui/lab'
import { AcfuShowMoreDatesButton } from '../../../../AcfuShowMoreDatesButton'

interface Props {
  availabilities: AvailabilitiesResponse | undefined
}

type DayFormData = Pick<
  AppointmentFormData,
  'clientStartDate' | 'clientStartTime' | 'customer_timezone' | 'month_date' | 'resource_ids' | 'specialist_id'
>

const Content = ({ availabilities }: Props) => {
  const { setValue, watch } = useFormContext<DayFormData>()
  const onChange = () => setValue('clientStartTime', null)

  const [specialist_id, customer_timezone] = watch(['specialist_id', 'customer_timezone'])
  const customerTimeZone = customer_timezone as string

  const [availabilityWindowInDays] = useFeatureFlags(['SCHEDULING_AVAILABILITY_WINDOW_IN_DAYS'])
  const { canShowMoreDates, isAcfuEligible, onShowMoreDates, windowInDays } = useAcfuProgressiveDisclosure(
    availabilityWindowInDays as number
  )
  const { data: now = new Date() } = useNow()
  // The shared SCHEDULING_AVAILABILITY_WINDOW_IN_DAYS flag has always meant "windowInDays - 1" here
  // (maxDate is exclusive) — harmless at 62 days, but the ACFU default of 3 needs to mean 3
  // selectable days literally, so only the ACFU-eligible branch skips the -1.
  const dateLimit = useMemo(
    () => startOfDayTimezoned(addDays(now, isAcfuEligible ? windowInDays : windowInDays - 1), customerTimeZone),
    [now, isAcfuEligible, windowInDays, customerTimeZone]
  )

  const { dateToISODateKey } = useDateFormat()
  const onMonthChange = useCallback((date: Date) => setValue('month_date', date), [setValue])

  const renderDay = (date: Date, _: Date[], pickersDayProps: PickersDayProps<unknown>) => {
    const dateKey = dateToISODateKey(date)

    const isDisabled = pickersDayProps.disabled
    if (isDisabled) return <CustomPickersDay {...pickersDayProps} dayState="DISABLED" />

    const noAvailabilityforDay =
      !availabilities ||
      Object.keys(availabilities).length === 0 ||
      !availabilities.dayAvailability ||
      !availabilities.dayAvailability[dateKey] ||
      !availabilities.resourceHasCalendarIntegration?.[specialist_id as string]
    if (noAvailabilityforDay) return <CustomPickersDay {...pickersDayProps} dayState="NOT_AVAILABLE" />

    const dayAvailabilities = availabilities.dayAvailability[dateKey]
    const specialistSlots = dayAvailabilities[specialist_id as string]
    const hasAvailabilitiesForSpecialist = Boolean(specialistSlots && specialistSlots.length > 0)

    return <CustomPickersDay {...pickersDayProps} dayState={hasAvailabilitiesForSpecialist ? 'AVAILABLE' : 'NOT_AVAILABLE'} />
  }

  return (
    <>
      <Calendar
        customerTimeZone={customerTimeZone}
        name="clientStartDate"
        maxDate={dateLimit}
        onChange={onChange}
        onMonthChange={onMonthChange}
        renderDay={renderDay}
      />
      {canShowMoreDates && <AcfuShowMoreDatesButton onExpand={onShowMoreDates} />}
    </>
  )
}

export { Content }
